import { getCookie, setCookie, updateCookie, text_truncate, getBrowserType, getTrackingNonce, normalizeCodePlaceholder } from '../core/utils.js';

// Handles forms with [data-mlat-form] attribute.
// Validation is driven entirely by HTML attributes (required, type, pattern, minlength, maxlength, title).
// Tracking uses the same mlat_inquiry_store endpoint as form-validation-id.js.

function getFieldLabel(input) {
  const group = input.closest('.form-group');
  const label = group && group.querySelector('label');
  return label ? label.textContent.trim() : 'Field ini';
}

function showError(input, message) {
  const group = input.closest('.form-group');
  if (!group) return;
  group.classList.remove('success');
  group.classList.add('error');
  const existing = group.querySelector('small.error');
  if (existing) existing.remove();
  const small = document.createElement('small');
  small.classList.add('error');
  small.textContent = message;
  group.appendChild(small);
}

function showSuccess(input) {
  const group = input.closest('.form-group');
  if (!group) return;
  group.classList.remove('error');
  const type = input.getAttribute('type');
  if (type !== 'checkbox' && type !== 'radio') group.classList.add('success');
  const existing = group.querySelector('small.error');
  if (existing) existing.remove();
}

const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

function validateField(input) {
  const type    = (input.getAttribute('type') || 'text').toLowerCase();
  const value   = input.value.trim();
  const label   = getFieldLabel(input);
  const pattern = input.getAttribute('pattern');
  const title   = input.getAttribute('title');
  const min     = parseInt(input.getAttribute('minlength'), 10) || 0;
  const max     = parseInt(input.getAttribute('maxlength'), 10) || Infinity;

  if (input.hasAttribute('required')) {
    if (type === 'checkbox' || type === 'radio') {
      if (!input.checked) { showError(input, title || `${label} harus dicentang`); return false; }
    } else if (value === '') {
      showError(input, title || `${label} harus diisi`);
      return false;
    }
  } else if (value === '') {
    showSuccess(input);
    return true;
  }

  if (type === 'email' || input.getAttribute('inputmode') === 'email') {
    if (!EMAIL_RE.test(value)) {
      showError(input, title || 'Format email tidak valid');
      return false;
    }
  }

  if (pattern) {
    const re = new RegExp(`^(?:${pattern})$`);
    if (!re.test(value)) {
      showError(input, title || `Format ${label} tidak valid`);
      return false;
    }
  }

  if (min > 0 && value.length < min) {
    showError(input, `${label} minimal ${min} karakter`);
    return false;
  }
  if (max < Infinity && value.length > max) {
    showError(input, `${label} maksimal ${max} karakter`);
    return false;
  }

  showSuccess(input);
  return true;
}

function validateForm(form) {
  const fields = Array.from(form.querySelectorAll('input, textarea, select'));
  let valid = true;
  fields.forEach(f => {
    if (f.type === 'hidden' || f.type === 'submit' || f.type === 'button') return;
    if (!validateField(f)) valid = false;
  });
  return valid;
}

function buildTimestamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return pad(d.getDate()) + pad(d.getMonth() + 1) + String(d.getFullYear()).slice(2)
    + pad(d.getHours()) + pad(d.getMinutes()) + d.getSeconds() + d.getMilliseconds();
}

function getDeviceType() {
  const ua = navigator.userAgent;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'Mobile' : 'Desktop';
}

// Ticket format is configurable per-site (wp-admin → Leads Attribution → Contact/Inquiry
// → Ticket Code Format), so the prefix can differ per brand (e.g. [Req.Jxxxx] for Jurnal,
// [Req.Oxxxx] for Officeless) without touching this file.
function buildTicketCode(ts) {
  const fmt = (window.mlatConfig && window.mlatConfig.contact && window.mlatConfig.contact.ticketFormat) || '[Req.J{{code}}]';
  return normalizeCodePlaceholder(fmt).replace('{{code}}', ts);
}

function fillTrackingFields(form, ts) {
  const url = new URL(window.location.href);
  const sp  = url.searchParams;
  const gc  = k => getCookie(k) || '';
  const gclid   = sp.get('gclid')   || gc('gclid');
  const msclkid = sp.get('msclkid') || gc('msclkid');

  const map = {
    utm_source:   sp.get('utm_source')   || gc('utm_source'),
    utm_medium:   sp.get('utm_medium')   || gc('utm_medium'),
    // Ticket code uses the same `ts` as the `short` column sent to our DB —
    // this is what lets a Zoho CRM record be matched back to its DB row.
    utm_campaign: buildTicketCode(ts),
    utm_term:     sp.get('utm_term')     || gc('utm_term'),
    utm_content:  sp.get('utm_content')  || gc('utm_content'),
    campaign_id:  sp.get('campaign_id')  || gc('campaign_id'),
    source_url:   window.location.href,
    // Same combined "gclid|msclkid" format as the `gclid` column sent to our
    // DB (see buildPayload() below), so this field stays consistent with it.
    gclid:        text_truncate(`${gclid}|${msclkid}`, 250, ''),
    device:       getDeviceType(),
  };

  // Zoho's HTML export names fields however Zoho generated them, which doesn't
  // always match our tracking key (e.g. this site's gclid field is id="gclid",
  // not name="gclid") — so match on name, id, OR a "form_" prefixed class
  // (e.g. class="form_gclid"), since some sites' Zoho exports only expose a
  // stable hook via class instead of id/name. querySelectorAll (not
  // querySelector) because a page can legitimately have more than one of
  // these for the same key; when it does, fill all of them.
  // TODO(debug): remove this console.log block once the gclid/device fill issue is confirmed fixed.
  console.log('[MLAT debug] fillTrackingFields() running on form', form);
  Object.entries(map).forEach(([name, value]) => {
    const selector = `input[name="${name}"], #${name}, .form_${name}`;
    const els = form.querySelectorAll(selector);
    console.log(`[MLAT debug] ${name}: selector="${selector}" matched ${els.length} element(s), value="${value}"`);
    els.forEach(el => {
      el.value = value;
    });
  });
}

// Some embedded forms (e.g. Zoho "Form Name" field, id="form_name") carry a
// human-readable name identifying which form/page this is (distinct from
// #form_id, which is a form-steps correlation ID, not a label). Falls back to
// the generic label for forms that don't have this field (Contact, General
// Inquiry, etc.) — behavior unchanged for those.
function getFormDisplayName(form) {
  const nameInput = form && form.querySelector('#form_name, [name="form_name"]');
  const value = nameInput && nameInput.value && nameInput.value.trim();
  return value || 'Form Submit';
}

function buildPayload(form, ts) {
  const url  = new URL(window.location.href);
  const sp   = url.searchParams;
  const gc   = k => getCookie(k) || '';
  const gclid   = sp.get('gclid')   || gc('gclid');
  const msclkid = sp.get('msclkid') || gc('msclkid');

  const cta_journey = gc('cta_journey');
  const label = `ID: inquiry-form, Label: ${getFormDisplayName(form)}`;
  const cta_journey_new = cta_journey ? cta_journey + '|' + label : label;

  if (getCookie('cta_journey')) {
    updateCookie('cta_journey', cta_journey_new);
  } else {
    setCookie('cta_journey', cta_journey_new, 31);
  }

  return {
    nonce:             getTrackingNonce(),
    short:             ts, // same `ts` used in fillTrackingFields()'s utm_campaign ticket — the join key
    gclid:             text_truncate(`${gclid}|${msclkid}`, 250, ''),
    journey:           text_truncate(gc('user_journey'), 250, ''),
    utm_term:          sp.get('utm_term')      || gc('utm_term'),
    matchtype:         sp.get('matchtype')     || gc('matchtype'),
    device:            getDeviceType(),
    button_label_id:   cta_journey_new,
    gaid:              gc('gaid') || (gc('_ga') ? gc('_ga').slice(6) : ''),
    utm_source:        sp.get('utm_source')    || gc('utm_source'),
    utm_medium:        sp.get('utm_medium')    || gc('utm_medium'),
    // The REAL campaign this visitor came from, read from the `utm_campaign`
    // cookie that utm-tracker.js/journey-tracker.js already maintain as the
    // aggregated, attributed value across the visit (fallback 'default' when
    // it's never been primed) — NOT the raw URL param, which only reflects
    // whatever this one page happened to carry. NOT the Zoho ticket code
    // either: that only replaces utm_campaign inside the Zoho-bound form
    // itself (see fillTrackingFields() below), so a CRM record can be matched
    // back to the `short` column here — the DB row keeps the actual
    // attribution source instead of being overwritten by it.
    utm_campaign:      gc('utm_campaign') || 'default',
    utm_content:       sp.get('utm_content')   || gc('utm_content'),
    campaign_id:       sp.get('campaign_id')   || gc('campaign_id'),
    banner_id:         gc('banner_journey'),
    waba_medium:       gc('waba_code'),
    browser:           getBrowserType(),
    domain:            window.location.host,
    gclid_journey:     gc('gclid_journey'),
    referrer:          gc('lead_referrer') || document.referrer || '',
    query_search_term: sp.get('query')         || gc('query_keyword'),
    visitor_id:        gc('visitor_id'),
    // Only inquiry-form submissions carry form_id/form_name — the multi-step
    // form-steps flow and the plain inquiry form share the same physical <form>,
    // so the hidden #form_id field (generated by the theme's form-steps.js) is
    // read straight from the DOM here rather than persisted anywhere. form_name
    // is the human-readable label (e.g. "General Inquiry - ID", "Partnership -
    // Reseller Form" via Zoho's own #form_name field) that maps to the DB's
    // dedicated `form_name` column shown in wp-admin — distinct from
    // button_label_id above, which is the CTA click-journey trail.
    form_id:           form ? (form.querySelector('#form_id, [name="form_id"]')?.value || '') : '',
    form_name:         form ? getFormDisplayName(form) : '',
  };
}

function sendTracking(payload) {
  // Prefer the server-baked URL (window.mlatConfig.ajaxUrl) over deriving it from
  // <link rel="https://api.w.org/">: on multilingual sites that link can come back
  // locale-prefixed (e.g. /en/wp-json/), and admin-ajax.php is never under that prefix.
  const wpApiLink = document.querySelector('link[rel="https://api.w.org/"]');
  const wpJsonBase = (wpApiLink && wpApiLink.href)
    ? wpApiLink.href.replace(/\/?$/, '/')
    : (window.location.origin + '/wp-json/');
  const ajaxUrl = (window.mlatConfig && window.mlatConfig.ajaxUrl)
    || (wpJsonBase.replace(/wp-json\/?$/, '').replace(/\/?$/, '/') + 'wp-admin/admin-ajax.php');

  return fetch(ajaxUrl + '?action=mlat_inquiry_store', {
    method: 'POST',
    // text/plain: CORS-safelisted (tidak pernah preflight); PHP baca php://input mentah.
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
    keepalive: true,
  }).then(r => r.ok ? r.json().catch(() => null) : null);
}

function initSingleForm(form) {
  // Guard against re-initializing the same <form> — initInquiryForms() is called
  // more than once per page load (waba-core.js runs runCoreInits() synchronously,
  // then again after initWabaTracking() settles). Without this guard, initSingleForm()
  // would attach a SECOND, independent 'click' listener to the same submit button on
  // the second call. Since the existing click handler below only calls
  // e.stopPropagation() (not stopImmediatePropagation()), that does NOT stop the other
  // listener registered on the SAME node from also firing — so a single real click
  // would run both listeners, calling sendTracking() twice and creating a duplicate
  // row in the DB for one submission.
  if (form.dataset.mlatInquiryBound === '1') return;
  form.dataset.mlatInquiryBound = '1';

  // Every form uses "await tracking, then submit": native submission navigates
  // to the third-party (Zoho) action URL, and a fire-and-forget request can be
  // cancelled by that navigation (notably in mobile in-app browsers). Previously
  // only form-block forms (.form-block__form) waited; now all of them do. A
  // failed DB insert is forwarded to GSheets server-side (mlat_gsheets_fallback),
  // so there is no JS-side GSheets send here and no duplicate rows.
  const TRACK_MAX_WAIT_MS = 3000;

  // Patch form.submit so tracking always fires regardless of what triggers the submit
  // (submit button click, form-steps.js calling form.submit() directly, etc.)
  const _originalSubmit = HTMLFormElement.prototype.submit.bind(form);
  form.submit = function () {
    // Some site themes bind their OWN click listener to the same submit button
    // for their own (pre-existing) form-validation/submission flow — e.g. the
    // esign theme's validate.min.js — and that listener also calls
    // form.submit() directly. Since form.submit is patched at the instance
    // level, every caller re-enters this function, so a single click can run
    // this body more than once and send duplicate tracking rows. Guard against
    // that regardless of who else is calling form.submit(); the flag clears
    // itself shortly after so a genuine, later resubmission still works.
    if (form.dataset.mlatSubmitting === '1') return;
    form.dataset.mlatSubmitting = '1';
    setTimeout(() => { delete form.dataset.mlatSubmitting; }, 5000);

    // Generated once and shared: this is what lets the [Req.Jxxxx] ticket
    // submitted to Zoho be matched back to the `short` column of the DB row.
    const ts = buildTimestamp();
    fillTrackingFields(form, ts);
    const trackingPromise = sendTracking(buildPayload(form, ts)).catch(() => {});

    // A form that submits into another tab/window keeps this page alive, so the
    // keepalive fetch can't be cancelled — and a delayed submit() would lose the
    // user gesture and get popup-blocked. Submit immediately in that case.
    const target = (form.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self' && target !== '_top' && target !== '_parent') {
      _originalSubmit();
      return;
    }

    // Wait for tracking to finish (bounded by a timeout so a slow/down endpoint
    // never blocks a legitimate submission indefinitely), THEN submit natively.
    const timeoutPromise = new Promise((resolve) => setTimeout(resolve, TRACK_MAX_WAIT_MS));
    Promise.race([trackingPromise, timeoutPromise]).then(() => {
      _originalSubmit();
    });
  };

  // Inline validation on blur
  form.querySelectorAll('input, textarea').forEach(input => {
    if (input.type === 'hidden' || input.type === 'submit') return;
    input.addEventListener('blur', () => validateField(input));
  });

  // Validation on submit button click — only for forms NOT using form-steps
  // (form-steps.js handles per-step validation itself and calls form.submit() directly)
  const submitBtn = form.querySelector('[type="submit"]');
  const hasFormSteps = !!form.closest('.form-steps__form');

  if (submitBtn && !hasFormSteps) {
    submitBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (!validateForm(form)) return;
      form.submit();
    });
  }
}

export function initInquiryForms() {
  document.querySelectorAll('form[data-mlat-form]').forEach(initSingleForm);
}
